import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useToast } from '../../components/Toast';
import { paymentFaq } from '../../data/support/paymentFaq';
import { SupportFaqList, SupportGuideLayout, SupportHelpFooter, SupportLinkList, openChannelTalk } from './SupportGuide';

export const PAYMENT_LINKS = [
  { to: '/subscription', title: '내 이용권·결제 내역', body: '남은 기간 확인, 증빙 서류, 환불 요청' },
  { to: '/bulk-purchase', title: '학교·단체 구매', body: '견적서·서류 받기, 여러 명 한 번에' },
];

const PaymentPage = () => {
  const { showToast } = useToast();

  return (
    <>
      <Helmet>
        <title>결제 및 구독 FAQ | 왓퀴즈 PRO 요금제 안내</title>
        <meta name="description" content="왓퀴즈 PRO 구독, 결제 방법, 환불 정책, 세금계산서 발급 등 결제 관련 자주 묻는 질문. 토스페이먼츠 안전 결제, 학교 예산 사용 가능." />
        <meta name="keywords" content="왓퀴즈 결제, PRO 구독, 환불 정책, 세금계산서, 학교 예산, 토스페이먼츠, 교사 AI 요금" />
        <meta property="og:title" content="결제 및 구독 FAQ | 왓퀴즈 PRO" />
        <meta property="og:description" content="왓퀴즈 PRO 결제, 환불, 세금계산서 등 자주 묻는 질문. 학교 예산 결제 가능." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://whatquiz.co.kr/support/payment" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "왓퀴즈 PRO 요금제는 어떻게 되나요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "3개월 플랜 60,000원(월 20,000원), 6개월 플랜 110,000원(월 18,333원, 24% 할인), 12개월 플랜 200,000원(월 16,667원, 31% 할인). 모든 가격은 부가세 포함입니다."
                }
              },
              {
                "@type": "Question",
                "name": "결제는 어떻게 하나요? 안전한가요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "토스페이먼츠를 통해 안전한 결제를 제공합니다. PCI-DSS Level 1 인증, SSL 암호화, 3D Secure 이중 보안을 적용하며, 국내 신용카드/체크카드로 결제 가능합니다."
                }
              },
              {
                "@type": "Question",
                "name": "자동 갱신되나요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "아니요. 왓퀴즈는 기간제 결제 시스템으로, 선택한 기간에 대해 한 번만 결제되며 자동 갱신되지 않습니다. 만료 전 연장을 원하시면 직접 선택하시면 됩니다."
                }
              },
              {
                "@type": "Question",
                "name": "환불이 가능한가요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "결제 후 7일 이내라면 사용 이력과 무관하게 100% 전액 자동 환불됩니다 (마이페이지 → 구독 관리 → 환불 요청). 7일 경과 후에는 자동 환불이 불가하며, 회사 귀책사유나 결제 오류 등 특별한 사정이 있는 경우 angel@plactical.com으로 문의 시 사안별로 검토합니다. 「전자상거래법」 제17조를 준수합니다."
                }
              },
              {
                "@type": "Question",
                "name": "영수증·거래명세서 같은 증빙 서류는 어디서 받나요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "마이페이지 → 구독 관리 → 결제 내역에서 결제 옆 '증빙 서류'를 누르면 결제 영수증, 거래명세서, 납품확인서(주문확인서 겸용), 견적서, 사업자등록증, 통장사본, 4대보험 완납증명서를 언제든 받을 수 있어요. ZIP 한 번에 받기와 행정실 메일 보내기를 지원하고, 결제 직후에는 거래명세서와 납품확인서를 메일로도 보내드려요."
                }
              },
              {
                "@type": "Question",
                "name": "세금계산서 발급이 가능한가요?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "신용·체크카드로 결제했거나 현금영수증을 받은 결제는 부가가치세법상 세금계산서를 따로 발행할 수 없어요. 카드 매출전표나 현금영수증이 공식 증빙이며, 행정실에는 결제 영수증과 거래명세서를 함께 내면 돼요. 세금계산서가 꼭 필요하면 학교·단체 구매(S2B 학교장터 포함)를 이용해주세요."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <SupportGuideLayout current="결제·이용권">
        <section className="sg__hero">
          <h1>결제·이용권 안내</h1>
          <p>한 번 결제하면 고른 기간만 쓰고, 자동으로 다시 결제되지 않아요</p>
          <Link to="/subscription/pro" className="sg__cta">PRO 요금 보기</Link>
        </section>

        <section className="sg__card">
          <h2>바로 가기</h2>
          <SupportLinkList items={PAYMENT_LINKS} />
        </section>

        <section className="sg__card">
          <h2>자주 묻는 질문</h2>
          <SupportFaqList faqs={paymentFaq} />
        </section>

        <SupportHelpFooter text="결제가 안 되거나 궁금하면" onChat={() => openChannelTalk(showToast)} />
      </SupportGuideLayout>
    </>
  );
};

export default PaymentPage;
